/**
 * Footer Component
 * Single-line footer with language dropdown and legal links.
 * Uses brand configuration for dynamic URLs (WL-303).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { designTokens } from '../../constants/designTokens';
import { useBrand, type BrandConfig } from '../../contexts/BrandContext';
import {
  LANGUAGE_LABELS,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
  useLanguage,
} from '../../i18n';

const POLICY_PATHS = {
  // Kept in lockstep with services/docs/legal-routes.json by the footer
  // contract test; docs builds also verify that every declared route exists.
  privacy: '/privacy-policy',
  terms: '/terms-and-conditions',
  biometric: '/biometric-disclosure',
};

const POLICY_SUBDOMAIN_PREFIXES = new Set([
  'portal',
  'www',
  'verify',
  'av',
  'api',
  'docs',
  'demo',
]);

const getPolicyOrigin = (candidate: string): string => {
  const url = new URL(candidate);

  // Staging publishes legal content on docs.staging.<brand>. The corresponding
  // bare staging host intentionally does not exist, unlike the production host.
  if (url.hostname.startsWith('docs.staging.')) {
    return url.origin;
  }

  const parts = url.hostname.split('.');
  const [first, ...rest] = parts;
  const baseHost =
    parts.length > 2 && POLICY_SUBDOMAIN_PREFIXES.has(first)
      ? rest.join('.')
      : url.hostname;
  const hostWithPort = url.port ? `${baseHost}:${url.port}` : baseHost;
  return `${url.protocol}//${hostWithPort}`;
};

const getPolicyBaseUrl = (brand: BrandConfig): string | null => {
  const candidates = [brand.docsUrl, brand.privacyUrl, brand.termsUrl].filter(
    (value): value is string => Boolean(value)
  );

  for (const candidate of candidates) {
    try {
      return getPolicyOrigin(candidate);
    } catch {
      // Ignore invalid URLs and continue to the next candidate.
    }
  }

  return null;
};

export const Footer = React.memo(function Footer() {
  const { language, setLanguage, t } = useLanguage();
  const brand = useBrand();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelect = useCallback(
    (lang: SupportedLanguage) => {
      setLanguage(lang);
      setDropdownOpen(false);
    },
    [setLanguage]
  );

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  if (!brand) {
    return null;
  }

  const policyBaseUrl = getPolicyBaseUrl(brand);
  const privacyUrl = policyBaseUrl
    ? `${policyBaseUrl}${POLICY_PATHS.privacy}`
    : brand.privacyUrl;
  const termsUrl = policyBaseUrl
    ? `${policyBaseUrl}${POLICY_PATHS.terms}`
    : brand.termsUrl;
  const biometricUrl = policyBaseUrl
    ? `${policyBaseUrl}${POLICY_PATHS.biometric}`
    : null;

  return (
    <footer className="footer">
      <div className="footer-row">
        {/* Language dropdown */}
        <div className="footer-lang-dropdown" ref={dropdownRef}>
          <button
            type="button"
            className="footer-lang-toggle"
            onClick={() => setDropdownOpen((prev) => !prev)}
            aria-expanded={dropdownOpen}
            aria-haspopup="listbox"
            aria-label={t('footer.languageLabel')}
          >
            {LANGUAGE_LABELS[language]}
            <svg
              className={`footer-lang-caret${dropdownOpen ? ' open' : ''}`}
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              aria-hidden="true"
            >
              <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {dropdownOpen && (
            <ul className="footer-lang-menu" role="listbox" aria-label={t('footer.languageLabel')}>
              {SUPPORTED_LANGUAGES.map((option) => (
                <li key={option} role="option" aria-selected={option === language}>
                  <button
                    type="button"
                    className={`footer-lang-item${option === language ? ' active' : ''}`}
                    onClick={() => handleSelect(option)}
                  >
                    {LANGUAGE_LABELS[option]}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <span className="footer-separator">|</span>

        {/* Legal links */}
        <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="footer-link">
          {t('footer.terms')}
        </a>
        <span className="footer-separator">|</span>
        <a href={privacyUrl} target="_blank" rel="noopener noreferrer" className="footer-link">
          {t('footer.privacy')}
        </a>
        {biometricUrl ? (
          <>
            <span className="footer-separator">|</span>
            <a href={biometricUrl} target="_blank" rel="noopener noreferrer" className="footer-link">
              {t('footer.biometric')}
            </a>
          </>
        ) : null}
      </div>

      <style>{`
        .footer {
          display: flex;
          justify-content: center;
          padding: ${designTokens.spacing.lg} ${designTokens.spacing.md};
          margin-top: auto;
          padding-top: ${designTokens.spacing['3xl']};
          font-size: ${designTokens.typography.fontSize.xs};
          color: ${designTokens.colors.text.secondary};
          background-color: transparent;
          width: 100%;
        }

        .footer-row {
          display: flex;
          align-items: center;
          gap: ${designTokens.spacing.xs};
          flex-wrap: wrap;
          justify-content: center;
        }

        /* Language dropdown */
        .footer-lang-dropdown {
          position: relative;
          display: inline-flex;
        }

        .footer-lang-toggle {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: transparent;
          border: none;
          color: ${designTokens.colors.text.secondary};
          font-size: inherit;
          cursor: pointer;
          padding: 2px 4px;
          line-height: 1;
        }

        .footer-lang-toggle:hover {
          color: ${designTokens.colors.brand.primary};
        }

        .footer-lang-caret {
          transition: transform ${designTokens.transitions.fast};
        }

        .footer-lang-caret.open {
          transform: rotate(180deg);
        }

        .footer-lang-menu {
          position: absolute;
          bottom: calc(100% + 4px);
          left: 0;
          list-style: none;
          margin: 0;
          padding: 4px 0;
          background: ${designTokens.colors.background.secondary};
          border: 1px solid ${designTokens.colors.border.default};
          border-radius: ${designTokens.borderRadius.md};
          box-shadow: ${designTokens.shadows.lg};
          z-index: ${designTokens.zIndex.dropdown};
          min-width: 120px;
        }

        .footer-lang-item {
          display: block;
          width: 100%;
          background: transparent;
          border: none;
          color: ${designTokens.colors.text.secondary};
          font-size: inherit;
          text-align: left;
          padding: 6px 12px;
          cursor: pointer;
          white-space: nowrap;
        }

        .footer-lang-item:hover {
          background: ${designTokens.colors.background.tertiary};
          color: ${designTokens.colors.text.primary};
        }

        .footer-lang-item.active {
          color: ${designTokens.colors.text.primary};
          font-weight: ${designTokens.typography.fontWeight.semibold};
        }

        /* Legal links */
        .footer-link {
          color: ${designTokens.colors.text.secondary};
          text-decoration: none;
          transition: color ${designTokens.transitions.normal};
        }

        .footer-link:hover {
          color: ${designTokens.colors.brand.primary};
          text-decoration: underline;
        }

        .footer-separator {
          color: ${designTokens.colors.text.tertiary};
          user-select: none;
        }

        /* Tablet */
        ${designTokens.media.tablet} {
          .footer {
            font-size: ${designTokens.typography.fontSize.sm};
            padding: ${designTokens.spacing.lg} ${designTokens.spacing.lg};
          }

          .footer-row {
            gap: ${designTokens.spacing.sm};
          }
        }

        /* Desktop */
        ${designTokens.media.desktop} {
          .footer {
            font-size: ${designTokens.typography.fontSize.sm};
            padding: ${designTokens.spacing.lg} ${designTokens.spacing.lg};
          }

          .footer-row {
            gap: ${designTokens.spacing.sm};
          }
        }
      `}</style>
    </footer>
  );
});
